import { PrivacySettings } from './Privacy';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PincodeLookup } from '@dru/types';
import {
  AddressBlock, AddressFieldset, ThemeToggle, addressForm, addressPayload,
  type AddressFormValue,
} from '@dru/ui';
import { api } from '../lib/api';
import { useOfflineQuery } from '../lib/useOfflineQuery';
import { useAuth } from '../lib/auth';

interface Profile {
  id: string;
  employeeCode: string;
  fullName: string;
  sex: string | null;
  dateOfBirth: string | null;
  mobile: string | null;
  email: string | null;
  bloodGroup: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  locality?: string | null;
  landmark?: string | null;
  city?: string | null;
  district?: string | null;
  state?: string | null;
  country?: string | null;
  pincode?: string | null;
  postOffice?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  formattedAddress?: string | null;
  mapsUrl?: string | null;
  plant: { name: string; city: string | null; state: string | null } | null;
  designation: { title: string } | null;
  jobRole: { title: string } | null;
  /** HR's photo of the worker, read-only here. Fetched through a grant. */
  hasPhoto?: boolean;
  photoUpdatedAt?: string | null;
}

/**
 * The worker's own photo, as an object URL, or null. The record says only
 * that one exists; the bytes come through a five-minute grant, like a
 * certificate, so the image never has an address of its own. Online only —
 * a photo is not something worth keeping on a phone that may be shared.
 */
function useOwnPhoto(hasPhoto: boolean | undefined, updatedAt: string | null | undefined): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!hasPhoto) { setSrc(null); return; }
    let live = true;
    let url: string | null = null;
    api.get<{ url: string; mimeType: string }>('/me/photo')
      .then(async (grant) => {
        const res = await fetch(grant.url, { credentials: 'include' });
        if (!res.ok) throw new Error('The photo could not be fetched.');
        return URL.createObjectURL(new Blob([await res.blob()], { type: grant.mimeType }));
      })
      .then((u) => { if (live) { url = u; setSrc(u); } else URL.revokeObjectURL(u); })
      .catch(() => { if (live) setSrc(null); });
    return () => { live = false; setSrc(null); if (url) URL.revokeObjectURL(url); };
  }, [hasPhoto, updatedAt]);
  return src;
}

export function ProfilePage() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const p = useOfflineQuery<Profile>('profile', () => api.get('/me/profile'));
  const [editing, setEditing] = useState(false);
  const photo = useOwnPhoto(p.data?.hasPhoto, p.data?.photoUpdatedAt);

  const rows: Array<[string, string | null | undefined]> = [
    ['Employee code', p.data?.employeeCode],
    ['Plant', p.data?.plant?.name],
    ['Job role', p.data?.jobRole?.title],
    ['Mobile', p.data?.mobile],
    ['Blood group', p.data?.bloodGroup],
  ];

  return (
    <div className="space-y-4">
      <h1 className="text-base font-semibold text-neutral-900">Your details</h1>

      <div className="rounded-xl border border-neutral-200 bg-white p-4">
        {/* The face HR put on the record, above the facts. Read-only: a
            photo that could be swapped from here would be an ID card a
            worker issues to themselves. */}
        {p.data && (
          <div className="mb-4 flex items-center gap-3">
            <span className="inline-flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-100 text-xl font-semibold text-primary-700 ring-1 ring-neutral-200">
              {photo
                ? <img src={photo} alt={`Photo of ${p.data.fullName}`} className="h-full w-full object-cover" />
                : <span aria-hidden>{p.data.fullName.trim().split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()}</span>}
            </span>
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-neutral-900">{p.data.fullName}</p>
              <p className="text-xs text-neutral-500">{p.data.hasPhoto ? 'Photo from your employer’s record' : 'No photo on your record'}</p>
            </div>
          </div>
        )}
        <dl className="space-y-3 text-sm">
          {rows.map(([label, val]) => (
            <div key={label} className="flex justify-between gap-4">
              <dt className="text-neutral-500">{label}</dt>
              <dd className="text-right text-neutral-900">{val ?? '—'}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* The one block of the record that is the worker's own to keep
          current. Typing the pincode fills the district, state and post
          office, the same as it does for HR. */}
      <div className="rounded-xl border border-neutral-200 p-4">
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-sm font-semibold text-neutral-900">Your address</h2>
          {!editing && p.data && (
            <button type="button" onClick={() => setEditing(true)} className="-mr-2 min-h-11 rounded-lg px-2 text-sm font-medium text-primary-700 underline underline-offset-2 active:bg-neutral-100">
              {p.data.pincode || p.data.addressLine1 ? 'Update' : 'Add'}
            </button>
          )}
        </div>
        {editing && p.data ? (
          <AddressEditor
            initial={addressForm(p.data)}
            onDone={() => { setEditing(false); p.reload(); }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <AddressBlock address={p.data} className="mt-2" empty="No address on file yet. Add it and your HR team has it on your record." />
        )}
      </div>

      {/* What a worker may change, and why the rest is fixed. Saying so is
          better than a disabled field with no explanation. */}
      <p className="text-xs text-neutral-500">
        Your name, employee code, plant and job role come from your employer's records. If
        something is wrong, ask your HR team to correct it — those details decide which health
        checks you are owed.
      </p>

      {/* Appearance sits above sign-out because it is the one setting a worker
          might come to this screen deliberately to change; sign-out is the
          destructive action and stays last. */}
      <div className="rounded-lg border border-neutral-200 bg-white p-4">
        <div className="text-sm font-medium text-neutral-800">Appearance</div>
        <p className="mt-0.5 text-xs text-neutral-500">
          Follows your phone's setting unless you choose otherwise.
        </p>
        <ThemeToggle className="mt-3 w-full justify-between" />
      </div>

      <PrivacySettings />

      <div>
        <button
          onClick={async () => { await logout(); nav('/login', { replace: true }); }}
          className="min-h-12 w-full rounded-lg border border-neutral-300 bg-white py-3 text-base font-medium text-neutral-800 active:bg-neutral-100"
        >
          Sign out
        </button>
        <p className="mt-2 text-center text-xs text-neutral-600">
          Signing out removes your saved reports and certificate from this phone. Do this before
          you hand the phone to someone else.
        </p>
      </div>
    </div>
  );
}

function AddressEditor({ initial, onDone, onCancel }: {
  initial: AddressFormValue; onDone: () => void; onCancel: () => void;
}) {
  const [value, setValue] = useState<AddressFormValue>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function save() {
    const local: Record<string, string> = {};
    if (value.pincode && value.pincode.length !== 6) local.pincode = 'Enter a valid 6-digit Indian pincode';
    setFieldErrors(local);
    if (Object.keys(local).length) return;
    setBusy(true); setError(null);
    try {
      await api.patch('/me/profile', addressPayload(value, { clearable: true }));
      onDone();
    } catch (e) {
      const err = e as { fieldErrors?: Record<string, string>; userMessage?: string; message?: string };
      if (err.fieldErrors) setFieldErrors(err.fieldErrors);
      setError(err.userMessage ?? err.message ?? 'Could not save your address.');
    } finally { setBusy(false); }
  }

  return (
    <div className="mt-3 space-y-3">
      <AddressFieldset
        value={value}
        onChange={setValue}
        compact
        errors={fieldErrors}
        lookupPincode={(pin) => api.get<PincodeLookup>(`/me/geo/pincodes/${pin}`)}
      />
      {error && <p role="alert" className="text-sm text-danger-600">{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} disabled={busy}
          className="min-h-12 flex-1 rounded-lg border border-neutral-300 py-3 text-sm font-medium text-neutral-800">
          Cancel
        </button>
        <button type="button" onClick={save} disabled={busy}
          className="min-h-12 flex-1 rounded-lg bg-primary-600 py-3 text-sm font-medium text-white disabled:opacity-60">
          {busy ? 'Saving…' : 'Save address'}
        </button>
      </div>
    </div>
  );
}
